import { useQuery } from '@tanstack/react-query'
import { Checkbox, Dialog, Input, SearchIcon, Text } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { trpc } from '../../../utils/trpc'

interface OrganizationWithFlagStatus {
	id: string
	name: string
	email: string
	enabled: boolean
}

interface FeatureFlagOrganizationsTabProps {
	flagId: string
	onEnabledCountChange: (count: number) => void
	onOrganizationsChange: (changes: Record<string, boolean>) => void
}

export const FeatureFlagOrganizationsTab = ({
	flagId,
	onEnabledCountChange,
	onOrganizationsChange,
}: FeatureFlagOrganizationsTabProps) => {
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
	const [pendingBulkAction, setPendingBulkAction] = useState<'check-all' | 'uncheck-all' | null>(null)
	const [localEnabledStates, setLocalEnabledStates] = useState<Record<string, boolean>>({})
	const [filterText, setFilterText] = useState('')

	const { data: organizations = [], isLoading } = useQuery({
		...trpc.admin.featureFlagsManagementPage.getOrganizations.queryOptions({ flagId }),
		enabled: !!flagId,
	})

	// Initialize local state from server data - merge with existing local state
	useEffect(() => {
		if (organizations.length > 0) {
			setLocalEnabledStates((prev) => {
				const newStates: Record<string, boolean> = {}
				organizations.forEach((org) => {
					// Preserve existing local state if it exists, otherwise use server state
					newStates[org.id] = org.id in prev ? prev[org.id] : (org.enabled ?? false)
				})
				return newStates
			})
		} else {
			// Reset state when organizations list is empty
			setLocalEnabledStates({})
		}
	}, [organizations])

	// Filter organizations based on search text
	const filteredOrganizations = useMemo(() => {
		if (!filterText.trim()) {
			return organizations
		}
		const searchLower = filterText.toLowerCase()
		return organizations.filter((org) => org.name.toLowerCase().includes(searchLower))
	}, [organizations, filterText])

	// Calculate if all filtered organizations are checked
	const allChecked = useMemo(() => {
		if (filteredOrganizations.length === 0) return false
		return filteredOrganizations.every((org) => {
			const state = localEnabledStates[org.id]
			return state === true // Explicitly check for true
		})
	}, [filteredOrganizations, localEnabledStates])

	// Update parent when count changes
	useEffect(() => {
		const count = organizations.filter((org) => {
			const state = localEnabledStates[org.id]
			return state === true // Explicitly check for true
		}).length
		onEnabledCountChange(count)
	}, [organizations, localEnabledStates, onEnabledCountChange])

	// Notify parent of changes
	useEffect(() => {
		onOrganizationsChange(localEnabledStates)
	}, [localEnabledStates, onOrganizationsChange])

	const handleToggle = (organizationId: string, enabled: boolean) => {
		setLocalEnabledStates((prev) => ({
			...prev,
			[organizationId]: enabled,
		}))
	}

	const handleBulkToggle = () => {
		// Determine action based on current state
		const action = allChecked ? 'uncheck-all' : 'check-all'
		setPendingBulkAction(action)
		setIsConfirmDialogOpen(true)
	}

	const confirmBulkToggle = () => {
		if (!pendingBulkAction) return

		const enabled = pendingBulkAction === 'check-all'
		const newStates: Record<string, boolean> = { ...localEnabledStates }

		// Update only filtered organizations
		filteredOrganizations.forEach((org) => {
			newStates[org.id] = enabled
		})

		setLocalEnabledStates(newStates)
		setIsConfirmDialogOpen(false)
		setPendingBulkAction(null)
	}

	const columns: Column<OrganizationWithFlagStatus>[] = [
		{
			field: 'name',
			label: 'Organization',
			colSpan: 4,
			render: (org: OrganizationWithFlagStatus) => (
				<Text variant='xsmall' weight='regular'>
					{org.name}
				</Text>
			),
		},
		{
			field: 'enabled',
			label: 'Enabled',
			render: (org: OrganizationWithFlagStatus) => {
				// Always use local state if it exists, otherwise fall back to server state
				const checked = org.id in localEnabledStates ? Boolean(localEnabledStates[org.id]) : Boolean(org.enabled)

				return (
					<Checkbox
						checked={checked}
						onChange={(e) => {
							const newValue = e.target.checked
							handleToggle(org.id, newValue)
						}}
					/>
				)
			},
		},
	]

	return (
		<div className='flex flex-col h-full'>
			<div className='mb-4 flex items-center justify-between flex-shrink-0'>
				<div className='flex items-center gap-2'>
					<Checkbox
						checked={allChecked}
						onChange={handleBulkToggle}
						disabled={isLoading || filteredOrganizations.length === 0}
					/>
					<Text variant='small' weight='semibold'>
						{allChecked ? 'Uncheck all' : 'Check all'}
						{filterText.trim() ? ' filtered' : ''}
					</Text>
				</div>
				<div className='w-64 relative'>
					<div className='absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none'>
						<SearchIcon size={16} className='fill-neutral-500' />
					</div>
					<Input
						placeholder='Filter organizations...'
						value={filterText}
						onChange={(e) => setFilterText(e.target.value)}
						className='pl-9'
					/>
				</div>
			</div>

			<div className='flex-1 min-h-0 overflow-y-auto'>
				<GenericTable<OrganizationWithFlagStatus, never>
					columns={columns}
					idField='id'
					data={{ data: filteredOrganizations }}
					showSearch={false}
					showPagination={false}
					onRowClick={undefined}
					emptyState={{
						title: filterText ? 'No organizations match your filter' : 'No organizations found',
						description: filterText ? 'Try adjusting your search criteria' : 'There are no organizations in the system',
					}}
				/>
			</div>

			<Dialog
				isOpen={isConfirmDialogOpen}
				onClose={() => {
					setIsConfirmDialogOpen(false)
					setPendingBulkAction(null)
				}}
				onConfirm={confirmBulkToggle}
				title={
					pendingBulkAction === 'check-all'
						? filterText.trim()
							? 'Check All Filtered Organizations'
							: 'Check All Organizations'
						: filterText.trim()
							? 'Uncheck All Filtered Organizations'
							: 'Uncheck All Organizations'
				}
				message={
					pendingBulkAction === 'check-all'
						? filterText.trim()
							? `Are you sure you want to enable this feature flag for all ${filteredOrganizations.length} filtered organization(s)? This will only affect the organizations currently visible in the table based on your filter.`
							: `Are you sure you want to enable this feature flag for all ${filteredOrganizations.length} organization(s)?`
						: filterText.trim()
							? `Are you sure you want to disable this feature flag for all ${filteredOrganizations.length} filtered organization(s)? This will only affect the organizations currently visible in the table based on your filter.`
							: `Are you sure you want to disable this feature flag for all ${filteredOrganizations.length} organization(s)?`
				}
				type='confirm'
				confirmLabel={pendingBulkAction === 'check-all' ? 'Check All' : 'Uncheck All'}
			/>
		</div>
	)
}
